import db from './index.js'

/**
 * Deep sanitizes a conversation object for IndexedDB storage by converting MongoDB-specific types
 * (like Decimal128, ObjectId) and reactive Proxies to plain JavaScript types that can be cloned.
 * Uses JSON serialization/deserialization to ensure all nested objects are plain.
 */
function sanitizeConversationForDB(conversation) {
    if (!conversation) return conversation

    // First, convert to plain object using JSON round-trip to break Proxy chains and clone deeply
    // This handles reactive Proxies from Pinia stores
    let sanitized
    try {
        sanitized = JSON.parse(JSON.stringify(conversation))
    } catch (e) {
        // If JSON round-trip fails, fall back to shallow copy and manual sanitization
        console.warn(
            'Failed to deep clone conversation, using shallow copy:',
            e
        )
        sanitized = { ...conversation }
    }

    // Sanitize paidMessaging if present
    if (sanitized.paidMessaging) {
        sanitized.paidMessaging = { ...sanitized.paidMessaging }

        // Ensure remaining is plain numbers
        if (sanitized.paidMessaging.remaining) {
            sanitized.paidMessaging.remaining = {
                free: Number(sanitized.paidMessaging.remaining.free) || 0,
                bonus: Number(sanitized.paidMessaging.remaining.bonus) || 0,
                purchased:
                    Number(sanitized.paidMessaging.remaining.purchased) || 0,
                total: Number(sanitized.paidMessaging.remaining.total) || 0,
            }
        }

        // Sanitize purchasedTotals - ensure all values are plain numbers
        if (sanitized.paidMessaging.purchasedTotals) {
            const totals = sanitized.paidMessaging.purchasedTotals
            sanitized.paidMessaging.purchasedTotals = {
                grantedMessages: Number(totals.grantedMessages) || 0,
                refundedMessages: Number(totals.refundedMessages) || 0,
                // JSON.parse already converts Decimal128-like objects, but ensure numbers
                grantedValueUSD: Number(totals.grantedValueUSD) || 0,
                refundedValueUSD: Number(totals.refundedValueUSD) || 0,
            }
        }

        // Ensure clientId and creatorId are strings (already handled by JSON.parse, but ensure)
        if (sanitized.paidMessaging.clientId) {
            sanitized.paidMessaging.clientId = String(
                sanitized.paidMessaging.clientId
            )
        }
        if (sanitized.paidMessaging.creatorId) {
            sanitized.paidMessaging.creatorId = String(
                sanitized.paidMessaging.creatorId
            )
        }

        // Ensure updatedAt is a string (Date -> ISO string)
        if (sanitized.paidMessaging.updatedAt) {
            sanitized.paidMessaging.updatedAt =
                sanitized.paidMessaging.updatedAt instanceof Date
                    ? sanitized.paidMessaging.updatedAt.toISOString()
                    : String(sanitized.paidMessaging.updatedAt)
        }
    }

    // Ensure _id is a string (ObjectId -> string)
    if (sanitized._id) {
        sanitized._id = String(sanitized._id)
    }

    // Ensure participants array contains strings
    if (Array.isArray(sanitized.participants)) {
        sanitized.participants = sanitized.participants.map((p) => String(p))
    }

    // Ensure participantDetails.userId are strings
    if (Array.isArray(sanitized.participantDetails)) {
        sanitized.participantDetails = sanitized.participantDetails.map(
            (pd) => ({
                ...pd,
                userId: pd.userId ? String(pd.userId) : pd.userId,
                _id: pd._id ? String(pd._id) : pd._id,
            })
        )
    }

    // Ensure Date fields are ISO strings
    if (sanitized.createdAt) {
        sanitized.createdAt =
            sanitized.createdAt instanceof Date
                ? sanitized.createdAt.toISOString()
                : String(sanitized.createdAt)
    }
    if (sanitized.updatedAt) {
        sanitized.updatedAt =
            sanitized.updatedAt instanceof Date
                ? sanitized.updatedAt.toISOString()
                : String(sanitized.updatedAt)
    }

    // Ensure lastMessage._id is a string if present
    if (sanitized.lastMessage) {
        if (typeof sanitized.lastMessage === 'object') {
            sanitized.lastMessage = {
                ...sanitized.lastMessage,
                _id: sanitized.lastMessage._id
                    ? String(sanitized.lastMessage._id)
                    : sanitized.lastMessage._id,
            }
        } else {
            sanitized.lastMessage = String(sanitized.lastMessage)
        }
    }

    return sanitized
}

export async function getAllConversations() {
    return await db.conversations.toArray()
}

export async function getConversation(conversationId) {
    return await db.conversations.get(conversationId)
}

export async function setConversations(conversations) {
    await db.conversations.clear()
    await db.conversations.bulkAdd(conversations.map(sanitizeConversationForDB))
}

export async function addConversation(conversation) {
    await db.conversations.put(sanitizeConversationForDB(conversation))
}

export async function updateConversation(conversation) {
    await db.conversations.put(sanitizeConversationForDB(conversation))
}

export async function deleteConversation(conversationId) {
    await db.conversations.delete(conversationId)
    await db.messages.where('conversationId').equals(conversationId).delete()
}

export async function updateConversationLastMessage(
    conversationId,
    lastMessage
) {
    if (!conversationId) return
    if (lastMessage && typeof lastMessage === 'object' && !lastMessage._id) {
        await db.conversations
            .where('_id')
            .equals(conversationId)
            .modify((conversation) => {
                if (conversation.lastMessage) {
                    conversation.lastMessage = {
                        ...conversation.lastMessage,
                        ...lastMessage,
                    }
                } else {
                    conversation.lastMessage = lastMessage
                }
            })
    } else {
        await db.conversations.where('_id').equals(conversationId).modify({
            lastMessage,
        })
    }
}

export async function getUnreadMessagesCounters(userId) {
    if (!userId) {
        return []
    }

    const conversations = await db.conversations.toArray()
    const unreadMessagesCounters = await Promise.all(
        conversations.map(async (conversation) => {
            const unreadCount = await db.messages
                .where('conversationId')
                .equals(conversation._id)
                .filter(
                    (message) =>
                        message.sender !== userId &&
                        !message.readBy?.includes(userId)
                )
                .count()
            return {
                conversationId: conversation._id,
                unreadMessages: unreadCount,
            }
        })
    )
    return unreadMessagesCounters
}

export async function findConversationByParticipantId(
    targetUserId,
    currentUserId
) {
    try {
        const conversation = await db.conversations
            .filter((conv) => {
                return (
                    conv.participantDetails &&
                    conv.participantDetails.some(
                        (p) => p.userId === targetUserId
                    ) &&
                    conv.participantDetails.some(
                        (p) => p.userId === currentUserId
                    )
                )
            })
            .first()
        return conversation || null
    } catch (error) {
        console.error(
            `Error finding conversation by participant ID ${targetUserId} in DB:`,
            error
        )
        return null
    }
}
